import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Skeleton,
  Avatar,
  IconButton,
  Tooltip,
  Button,
  Checkbox,
  Pagination,
  MenuItem,
  Select,
  Stack,
  Chip,
  FormControl,
  InputLabel,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Autocomplete,
  TextField,
  useTheme,
  Snackbar,
  FormControlLabel,
} from "@mui/material";
import TableChartIcon from "@mui/icons-material/TableChart";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from "@mui/icons-material/Search";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import EventIcon from "@mui/icons-material/Event";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { alpha } from "@mui/material/styles";
import CompanyResearch from "./CompanyResearch";
import SalesOutreachShell from "./SalesOutreachShell";
import { useLanguage } from '../context/LanguageContext';
import ContactsIcon from '@mui/icons-material/Contacts';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import logger from './../utils/logger.js';
import useOutreachTableSettings from '../hooks/useOutreachTableSettings';
import ColumnSettingsPanel from './ColumnSettingsPanel';

// Helper function to highlight the search term in text
const highlightSearchTerm = (text, searchTerm) => {
  if (!searchTerm || !text) return text;
  
  const searchRegex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = String(text).split(searchRegex);
  
  if (parts.length <= 1) return text;
  
  return (
    <>
      {parts.map((part, index) => {
        if (index % 2 === 0) {
          return part;
        } else {
          return (
            <span 
              key={index} 
              style={{ 
                backgroundColor: '#FFEB3B', 
                padding: '2px 0',
                fontWeight: 'bold'
              }}
            >
              {part}
            </span>
          );
        }
      })}
    </>
  );
};

const DataGrid = ({
  data = [],
  columns = [],
  columnTypes = {},
  loading,
  error,
  totalRows = 0,
  currentPage = 1,
  pageSize = 10,
  totalPages = 1,
  onPageChange,
  onPageSizeChange,
  onDataChange = () => {
    logger.log("Default onDataChange called. Consider passing a proper refresh function.");
  },
  onSelectionChange = () => {
    logger.log("Default onSelectionChange called. Consider passing a proper selection handler.");
  },
  selectedRecords = [],
  searchTerm = "",
  searchParams = {},
  onSort,
}) => {
  const { user, isSupervisor } = useAuth();
  const theme = useTheme();
  const [lastChecked, setLastChecked] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [researchDialogOpen, setResearchDialogOpen] = useState(false);
  const [outreachDialogOpen, setOutreachDialogOpen] = useState(false);
  const [selectedRecordsData, setSelectedRecordsData] = useState([]);
  const [bulkClaimDialogOpen, setBulkClaimDialogOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [removeExistingAssignments, setRemoveExistingAssignments] = useState(false);
  const [assignAllRecords, setAssignAllRecords] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [supervisedUsernames, setSupervisedUsernames] = useState([]); // Store supervised user usernames for supervisors
  const { t } = useLanguage();
  const [refreshNotification, setRefreshNotification] = useState({ open: false, message: '', count: 0 });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editedFields, setEditedFields] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveNotification, setSaveNotification] = useState({ open: false, message: '', severity: 'success' });
  
  // Use selectedRecords for consistency
  const selectedRows = selectedRecords;
  const setSelectedRows = onSelectionChange;

  // Debug logging for data
  useEffect(() => {
    logger.log('DataGrid received data:', {
      totalRecords: data.length,
      sampleRecord: data[0],
      claimsInSample: data[0]?.claims || [],
      outreachStatus: data[0]?.outreach_status || 'No outreach data'
    });

    // Check for any records with outreach data
    const recordsWithOutreach = data.filter(record => 
      record.outreach_status && 
      record.outreach_status.has_outreach === true
    );
    
    logger.log(`Found ${recordsWithOutreach.length} records with outreach data:`, 
      recordsWithOutreach.map(r => r.record_id));
      
    // Look for a record that might be the problematic one
    // If we find any records with interesting company names, check their outreach status
    setTimeout(() => {
      // Check outreach status for records with names that might be special
      const interestingRecords = data.filter(record => 
        record.Instellingsnaam && 
        (record.Instellingsnaam.includes("Example") || 
         record.Instellingsnaam.includes("Test") ||
         record.Instellingsnaam.includes("Demo"))
      );
      
      if (interestingRecords.length > 0) {
        logger.log("Found potentially interesting records:", 
          interestingRecords.map(r => ({ id: r.record_id, name: r.Instellingsnaam })));
          
        // Check the first interesting record
        const recordToCheck = interestingRecords[0];
        api.checkOutreachStatus(recordToCheck.record_id)
          .then(result => {
            logger.log(`Diagnostic check for ${recordToCheck.Instellingsnaam}:`, result);
            
            // Compare with what we actually received
            const receivedStatus = recordToCheck.outreach_status;
            logger.log("Actual data received in frontend:", receivedStatus);
            logger.log("Difference between API and frontend data:", {
              hasMeetingDateInAPI: result.outreaches && result.outreaches.length > 0 ? 
                                   !!result.outreaches[0].meeting_date : false,
              hasMeetingDateInFrontend: !!receivedStatus?.meeting_date,
              meetingDateFromAPI: result.outreaches && result.outreaches.length > 0 ? 
                                 result.outreaches[0].meeting_date : null,
              meetingDateInFrontend: receivedStatus?.meeting_date
            });
          })
          .catch(err => {
            logger.error("Error during diagnostic check:", err);
          });
      } else if (data.length > 0) {
        // If no interesting records found but we have data, check the first record
        const recordToCheck = data[0];
        api.checkOutreachStatus(recordToCheck.record_id)
          .then(result => {
            logger.log(`Diagnostic check for first record:`, result);
            
            // Compare with what we actually received
            const receivedStatus = recordToCheck.outreach_status;
            logger.log("Actual data received in frontend:", receivedStatus);
            logger.log("Difference between API and frontend data:", {
              hasMeetingDateInAPI: result.outreaches && result.outreaches.length > 0 ? 
                                   !!result.outreaches[0].meeting_date : false,
              hasMeetingDateInFrontend: !!receivedStatus?.meeting_date,
              meetingDateFromAPI: result.outreaches && result.outreaches.length > 0 ? 
                                 result.outreaches[0].meeting_date : null,
              meetingDateInFrontend: receivedStatus?.meeting_date
            });
          })
          .catch(err => {
            logger.error("Error during diagnostic check:", err);
          });
      }
    }, 1000); // Delay by 1 second to avoid too many concurrent requests
  }, [data]);

  // Fetch available users when bulk claim dialog opens
  useEffect(() => {
    const fetchUsers = async () => {
      if (bulkClaimDialogOpen && (user?.is_admin || isSupervisor)) {
        try {
          if (user?.is_admin) {
            // Admins see all users
            const response = await api.get('/api/admin/users');
            if (response.data.success) {
              setAvailableUsers(response.data.users);
            }
          } else if (isSupervisor) {
            // Supervisors see only themselves and their supervised users
            const [assignedUsersResponse, currentUserResponse] = await Promise.all([
              api.get('/api/supervisor/assigned-users'),
              api.get('/api/admin/users') // Get current user info
            ]);
            
            const supervisedUsers = assignedUsersResponse.data.success 
              ? assignedUsersResponse.data.users 
              : [];
            
            // Get current user info
            const allUsers = currentUserResponse.data.success 
              ? currentUserResponse.data.users 
              : [];
            const currentUserInfo = allUsers.find(u => u.username === user?.username);
            
            // Combine supervised users with current user
            const allowedUsers = [];
            if (currentUserInfo) {
              allowedUsers.push(currentUserInfo);
            }
            supervisedUsers.forEach(supervisedUser => {
              // Avoid duplicates
              if (!allowedUsers.find(u => u.username === supervisedUser.username)) {
                allowedUsers.push(supervisedUser);
              }
            });
            
            setAvailableUsers(allowedUsers);
          }
        } catch (error) {
          logger.error('Error fetching users:', error);
        }
      }
    };
    fetchUsers();
  }, [bulkClaimDialogOpen, user?.is_admin, isSupervisor, user?.username]);

  // Fetch all matching record IDs based on current filters/search
  const fetchAllMatchingRecordIds = async () => {
    try {
      // Use the filtered total count (totalRows) as pageSize to get exactly the filtered records
      // Add a buffer to ensure we get all records even if count is slightly off
      const pageSizeToUse = totalRows > 0 ? totalRows + 100 : 1000000;
      
      const requestParams = {
        page: 1,
        pageSize: pageSizeToUse,
        search: searchParams.search || searchTerm || '',
        sortField: searchParams.sortField || '',
        sortOrder: searchParams.sortOrder || 'asc',
        filters: searchParams.filters || [],
        get_total_count: false,
        refresh_cache: false,
        include_outreach: false // We don't need outreach data for IDs
      };

      logger.log('Fetching all matching record IDs with params:', {
        search: requestParams.search,
        filters: requestParams.filters,
        filtersCount: requestParams.filters.length,
        pageSize: requestParams.pageSize,
        currentTotalRows: totalRows
      });

      // Log each filter for debugging
      if (requestParams.filters && requestParams.filters.length > 0) {
        logger.log('Filters being applied:', JSON.stringify(requestParams.filters, null, 2));
      } else {
        logger.warn('No filters provided - will return all records matching user permissions');
      }

      const response = await api.post('/search', requestParams);

      if (response.data.success && response.data.data) {
        // Use the 'total' field which represents the filtered count (after filters are applied)
        const responseTotal = response.data.total || 0;
        // total_records is the total in DB before filters
        const totalInDB = response.data.total_records || 0;
        
        // Extract all record IDs from the response
        let allIds = response.data.data.map(record => record.record_id).filter(id => id != null);
        
        logger.log(`Fetched records for bulk assign`, {
          fetchedCount: allIds.length,
          responseTotalFiltered: responseTotal,
          totalInDB: totalInDB,
          expectedFilteredCount: totalRows,
          filtersApplied: requestParams.filters.length
        });
        
        // CRITICAL: Use the filtered total from the response to limit results
        // The backend applies filters and returns the filtered count in 'total'
        // Even if we requested a large pageSize, we should only use 'total' records
        let finalCount = responseTotal;
        
        // If responseTotal doesn't match totalRows (the filtered count shown in UI), 
        // use totalRows as it's more reliable (it's what the user sees)
        if (totalRows > 0 && responseTotal !== totalRows) {
          logger.warn(`Response total (${responseTotal}) doesn't match UI filtered count (${totalRows}). Using UI count.`);
          finalCount = totalRows;
        }
        
        // If responseTotal is 0 or suspiciously high (matches totalInDB), use totalRows
        if (responseTotal === 0 || (requestParams.filters && requestParams.filters.length > 0 && responseTotal === totalInDB && totalInDB > 0)) {
          if (totalRows > 0) {
            logger.warn(`Response total (${responseTotal}) appears incorrect. Using UI filtered count (${totalRows}) instead.`);
            finalCount = totalRows;
          } else {
            logger.error(`Cannot determine filtered count. Response total: ${responseTotal}, UI total: ${totalRows}`);
          }
        }
        
        // Limit results to the correct filtered count
        if (finalCount > 0 && allIds.length > finalCount) {
          logger.warn(`Got ${allIds.length} records but filtered total should be ${finalCount}. Limiting to ${finalCount} records.`);
          allIds = allIds.slice(0, finalCount);
        } else if (allIds.length < finalCount && finalCount > 0) {
          logger.warn(`Got ${allIds.length} records but expected ${finalCount}. May need to fetch more pages.`);
        }
        
        // Final validation
        if (requestParams.filters && requestParams.filters.length > 0 && allIds.length === totalInDB && totalInDB > 0) {
          logger.error(`CRITICAL: Filters may not be applied! Got ${allIds.length} records (all ${totalInDB} records in DB) despite ${requestParams.filters.length} filters.`);
          logger.error('Filters sent:', JSON.stringify(requestParams.filters, null, 2));
        }
        
        logger.log(`Returning ${allIds.length} record IDs for bulk assign (expected filtered count: ${finalCount}, UI shows: ${totalRows})`);
        return allIds;
      }
      logger.warn('No data returned from search endpoint');
      return [];
    } catch (error) {
      logger.error('Error fetching all matching record IDs:', error);
      logger.error('Request params were:', {
        search: searchParams.search || searchTerm || '',
        filters: searchParams.filters || [],
        filtersCount: (searchParams.filters || []).length,
        filtersDetail: JSON.stringify(searchParams.filters || [], null, 2)
      });
      return [];
    }
  };

  // Handle bulk claim submission
  const handleBulkClaimSubmit = async () => {
    if (selectedUsers.length === 0) return;

    setIsRefreshing(true);

    let recordIdsToAssign = selectedRows;
    let recordCount = selectedRows.length;

    // If "assign all records" is checked, fetch all matching record IDs
    if (assignAllRecords) {
      recordIdsToAssign = await fetchAllMatchingRecordIds();
      recordCount = recordIdsToAssign.length;
      
      if (recordIdsToAssign.length === 0) {
        setSaveNotification({
          open: true,
          message: 'No records found matching the current filters.',
          severity: 'warning'
        });
        setIsRefreshing(false);
        return;
      }
    }

    if (recordIdsToAssign.length === 0) {
      setSaveNotification({
        open: true,
        message: 'Please select at least one record or enable "Assign all records".',
        severity: 'warning'
      });
      setIsRefreshing(false);
      return;
    }

    // Store values before clearing
    const userCount = selectedUsers.length;
    const selectedUserNames = selectedUsers.map(user => user.username);

    try {
      const response = await api.post('/bulk-assign', {
        record_ids: recordIdsToAssign,
        usernames: selectedUserNames,
        remove_existing: removeExistingAssignments
      });

      if (response.data.success) {
        // Show success message
        logger.log('Bulk claim successful:', response.data.message);
        
        // Close dialog and clear selections first
        setBulkClaimDialogOpen(false);
        setSelectedUsers([]);
        setRemoveExistingAssignments(false);
        setAssignAllRecords(false);
        
        // Clear selected rows before refresh
        if (typeof setSelectedRows === 'function') {
          setSelectedRows([]);
        }
        
        // Refresh data and wait for it to complete
        if (typeof onDataChange === 'function') {
          try {
            await onDataChange();
            logger.log('Data refreshed after bulk assign');
          } catch (refreshError) {
            logger.error('Error refreshing data after bulk assign:', refreshError);
          }
        }
        
        // Show success notification
        setSaveNotification({
          open: true,
          message: response.data.removed_count > 0
            ? `Successfully assigned ${recordCount} record(s) to ${userCount} user(s). Removed ${response.data.removed_count} existing assignment(s).`
            : `Successfully assigned ${recordCount} record(s) to ${userCount} user(s)`,
          severity: 'success'
        });
      } else {
        logger.error('Bulk claim failed:', response.data.message);
        setSaveNotification({
          open: true,
          message: response.data.message || 'Failed to assign records',
          severity: 'error'
        });
      }
    } catch (error) {
      logger.error('Error in bulk claim:', error);
      setSaveNotification({
        open: true,
        message: error.response?.data?.message || 'Error assigning records. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Column settings hook
  const {
    settings: tableSettings,
    displayColumns: settingsDisplayColumns,
    updateSettings: updateTableSettings,
    applyPreset,
    resetToDefault,
    toggleColumn,
    reorderColumns,
    setColumnWidth,
    togglePinFirstColumn,
    SYSTEM_COLUMNS,
  } = useOutreachTableSettings(columns);

  // Column settings panel state
  const [columnPanelAnchor, setColumnPanelAnchor] = useState(null);
  const columnPanelOpen = Boolean(columnPanelAnchor);

  // Resize state
  const resizingRef = useRef(null);

  const handleResizeStart = useCallback((e, columnName) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = tableSettings?.columnWidths?.[columnName] || 150;

    const onPointerMove = (moveEvent) => {
      const diff = moveEvent.clientX - startX;
      const newWidth = Math.max(60, startWidth + diff);
      resizingRef.current = { columnName, width: newWidth };
      const cells = document.querySelectorAll(`[data-col="${columnName}"]`);
      cells.forEach((cell) => {
        cell.style.width = `${newWidth}px`;
        cell.style.minWidth = `${newWidth}px`;
        cell.style.maxWidth = `${newWidth}px`;
      });
    };

    const onPointerUp = () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      if (resizingRef.current) {
        setColumnWidth(resizingRef.current.columnName, resizingRef.current.width);
        resizingRef.current = null;
      }
    };

    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
  }, [tableSettings, setColumnWidth]);

  // Final ordered visible columns driven by settings
  const visibleColumns = settingsDisplayColumns;

  // Function to check if a row is selected
  const isRowSelected = (id) => selectedRows.includes(id);

  // Fetch supervised users for supervisors
  useEffect(() => {
    const fetchSupervisedUsers = async () => {
      if (isSupervisor && !user?.is_admin) {
        try {
          const response = await api.get('/api/supervisor/assigned-users');
          if (response.data.success && response.data.users) {
            const usernames = response.data.users.map(u => u.username);
            setSupervisedUsernames(usernames);
            logger.log('Supervisor supervised users:', usernames);
          }
        } catch (error) {
          logger.error('Error fetching supervised users:', error);
        }
      }
    };
    fetchSupervisedUsers();
  }, [isSupervisor, user?.is_admin]);

  // Helper function to check if current user has a claim on a record
  const hasClaimByCurrentUser = (row) => {
    // Check if this record has claims array
    if (row.claims && row.claims.length > 0) {
      // Check if any claim is by the current user
      return row.claims.some(claim => claim.claimed_by === user?.username);
    }
    // Fallback to the old method for backwards compatibility
    return row.claimed_by === user?.username;
  };

  // Helper function to check if a record is claimed by a supervised user (for supervisors)
  const isClaimedBySupervisedUser = (row) => {
    if (!isSupervisor || user?.is_admin || supervisedUsernames.length === 0) {
      return false;
    }
    
    // Check if this record has claims array
    if (row.claims && row.claims.length > 0) {
      // Check if any claim is by a supervised user
      return row.claims.some(claim => supervisedUsernames.includes(claim.claimed_by));
    }
    
    // Fallback to the old method for backwards compatibility
    return row.claimed_by && supervisedUsernames.includes(row.claimed_by);
  };

  // Handle row click and multi-selection with shift key
  const handleRowClick = (event, id, index) => {
    if (!id) return; // Skip if no record_id

    // Find the current row in the data
    const currentRow = data.find(row => row.record_id === id);
    
    // Check if the row is claimed by another user
    const isClaimed = currentRow && (currentRow.claimed === "Yes" || currentRow.claimed === true || currentRow.claimed === "true");
    const claimedByCurrentUser = isClaimed && hasClaimByCurrentUser(currentRow);
    const claimedBySupervisedUser = isClaimed && isClaimedBySupervisedUser(currentRow);
    const userIsAdmin = user?.is_admin;
    
    // If claimed by another user and user is not admin or supervisor of that user, don't allow selection
    if (isClaimed && !claimedByCurrentUser && !userIsAdmin && !claimedBySupervisedUser) return;

    if (event.shiftKey && lastChecked !== null) {
      // Get all unclaimed rows between lastChecked and current index
      const start = Math.min(lastChecked, index);
      const end = Math.max(lastChecked, index);

      const newSelected = [...selectedRows];

      // Add all rows between start and end that are not claimed or user is admin/supervisor
      for (let i = start; i <= end; i++) {
        if (data[i] && data[i].record_id) {
          const rowIsClaimed = data[i].claimed === "Yes" || data[i].claimed === true || data[i].claimed === "true";
          const rowClaimedByCurrentUser = rowIsClaimed && hasClaimByCurrentUser(data[i]);
          const rowClaimedBySupervisedUser = rowIsClaimed && isClaimedBySupervisedUser(data[i]);
          
          // Only select if row is either unclaimed, claimed by current user, claimed by supervised user, or user is admin
          if (!rowIsClaimed || rowClaimedByCurrentUser || userIsAdmin || rowClaimedBySupervisedUser) {
            if (!newSelected.includes(data[i].record_id)) {
              newSelected.push(data[i].record_id);
            }
          }
        }
      }

      setSelectedRows(newSelected);
    } else {
      // Toggle selection for this row
      const selectedIndex = selectedRows.indexOf(id);
      let newSelected = [];

      if (selectedIndex === -1) {
        // Add to selection
        newSelected = [...selectedRows, id];
      } else {
        // Remove from selection
        newSelected = selectedRows.filter((rowId) => rowId !== id);
      }

      setSelectedRows(newSelected);
    }

    setLastChecked(index);
  };

  // Format cell value based on column type
  const formatCellValue = (value, type) => {
    if (value === undefined || value === null) return "";

    let formattedValue;
    switch (type) {
      case "date":
        formattedValue = new Date(value).toLocaleDateString();
        break;
      case "boolean":
        formattedValue = value ? "Yes" : "No";
        break;
      default:
        formattedValue = String(value);
    }
    
    return highlightSearchTerm(formattedValue, searchTerm);
  };

  // Handle manual refresh - Update this function with better async handling
  const handleRefresh = async () => {
    if (isRefreshing) return; // Prevent multiple refresh calls
    
    setIsRefreshing(true);
    logger.log("Starting refresh, current total rows:", totalRows);
    
    try {
      // Check that onDataChange is a function before calling it
      if (typeof onDataChange === 'function') {
        logger.log("Calling onDataChange function to refresh data");
        const result = await onDataChange(); // Call the parent's data refresh function
        
        // Get the updated row count, either from the result or from the current state
        const updatedCount = result || totalRows;
        logger.log("Refresh completed, updated total rows:", updatedCount);
        
        // Show notification with the updated count
        setRefreshNotification({
          open: true,
          message: `Data refreshed successfully`,
          count: updatedCount
        });
      } else {
        logger.error('onDataChange is not a function. Cannot refresh data.');
        setRefreshNotification({
          open: true,
          message: 'Error refreshing data. Please try again.',
          count: 0
        });
      }
    } catch (error) {
      logger.error('Error refreshing data:', error);
      setRefreshNotification({
        open: true,
        message: 'Error refreshing data. Please try again.',
        count: 0
      });
    } finally {
      setIsRefreshing(false);
    }
  };
  
  // Add a function to close the notification
  const handleCloseNotification = () => {
    setRefreshNotification({
      ...refreshNotification,
      open: false
    });
  };

  // Pinned column name
  const pinnedColumn = tableSettings?.pinFirstColumn !== false ? 'Instellingsnaam' : null;

  // Render claimed cell with profile picture
  const renderClaimedCell = (row) => {
    // Get claims array from the row data
    const claims = row.claims || [];
    
    // Debug logging
    logger.log('Record claims:', {
      recordId: row.record_id,
      claims: claims,
      claimsLength: claims.length
    });
    
    if (claims.length === 0) {
      return (
        <Chip
          label="Unclaimed"
          size="small"
          sx={{ bgcolor: "background.default" }}
        />
      );
    }

    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {claims.map((claim, index) => {
          const claimedByCurrentUser = claim.claimed_by === user?.username;
          return (
            <Tooltip 
              key={`${claim.claimed_by}-${index}`}
              title={`Claimed by ${claim.claimed_by} on ${new Date(claim.claimed_date).toLocaleDateString()}`}
            >
              <Stack 
                direction="row" 
                spacing={0.5} 
                alignItems="center"
                sx={{
                  bgcolor: claimedByCurrentUser ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                  p: 0.5,
                  borderRadius: 1,
                }}
              >
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: '0.875rem',
                    bgcolor: claimedByCurrentUser ? "primary.main" : "text.secondary",
                  }}
                >
                  {claim.claimed_by ? (
                    claim.claimed_by.charAt(0).toUpperCase()
                  ) : (
                    <PersonIcon sx={{ fontSize: '1rem' }} />
                  )}
                </Avatar>
                <Typography 
                  variant="body2" 
                  noWrap 
                  sx={{ 
                    maxWidth: 80,
                    color: claimedByCurrentUser ? 'primary.main' : 'text.secondary',
                    fontWeight: claimedByCurrentUser ? 600 : 400
                  }}
                >
                  {claim.claimed_by}
                </Typography>
              </Stack>
            </Tooltip>
          );
        })}
      </Stack>
    );
  };

  // Handle claiming selected records
  const handleClaimRecords = async () => {
    if (selectedRows.length === 0) return;

    try {
      setIsRefreshing(true);
      const response = await api.post("/bulk-claim", {
        record_ids: selectedRows,
      });

      if (response.data.success) {
        // Show message with claimed/unclaimed counts
        const message = response.data.message || `Operation completed successfully.`;
        // We could add a toast or snackbar message here
        logger.log(message);
        
        // Refresh data to show updated claim status if onDataChange is provided
        if (typeof onDataChange === 'function') {
          onDataChange();
        } else {
          logger.warn('onDataChange is not a function. Data will not be refreshed automatically.');
          // Fallback - you might want to implement a different refresh strategy here
          // For example, refresh the page or show a message to the user
        }
      } else {
        logger.error("Claim operation failed:", response.data.message);
      }
    } catch (error) {
      logger.error("Error claiming records:", error);
      // Could add error message display here
    } finally {
      setIsRefreshing(false);
    }
  };

  // Add function to handle research button click
  const handleResearchClick = () => {
    if (selectedRows.length === 0) {
      // Show an alert or message that no rows are selected
      return;
    }

    // Get the full data for selected records
    const recordsData = data.filter(row => selectedRows.includes(row.record_id));
    setSelectedRecordsData(recordsData);
    setResearchDialogOpen(true);
  };

  // Add function to handle outreach button click
  const handleOutreachClick = () => {
    if (selectedRows.length === 0) {
      return;
    }

    const recordData = data.find(row => row.record_id === selectedRows[0]);
    if (recordData) {
      setSelectedRecordsData([recordData]);
      setOutreachDialogOpen(true);
    }
  };

  const handleRowDoubleClick = (row) => {
    if (row) {
      setSelectedRecordsData([row]);
      setOutreachDialogOpen(true);
    }
  };

  // Handle opening edit dialog
  const handleEditClick = (event, row) => {
    event.stopPropagation(); // Prevent row selection
    setEditingRecord(row);
    setEditedFields({ ...row }); // Initialize with current values
    setEditDialogOpen(true);
  };

  // Handle closing edit dialog
  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditingRecord(null);
    setEditedFields({});
  };

  // Handle field change in edit dialog
  const handleFieldChange = (fieldName, value) => {
    setEditedFields(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Handle saving edited record
  const handleSaveEdit = async () => {
    if (!editingRecord) return;

    setIsSaving(true);
    try {
      // Get the fields that have changed
      const changedFields = {};
      Object.keys(editedFields).forEach(key => {
        if (editedFields[key] !== editingRecord[key]) {
          changedFields[key] = editedFields[key];
        }
      });

      // Skip system fields that shouldn't be edited
      const skipFields = ['record_id', 'claimed', 'claimed_by', 'Claimed_by', 'claims', 'outreach_status', 'file_id', 'created_at', 'updated_at', 'updated_by'];
      
      // Update each changed field
      const updatePromises = Object.keys(changedFields)
        .filter(key => !skipFields.includes(key))
        .map(key => {
          return api.updateRecordField(editingRecord.record_id, key, changedFields[key]);
        });

      const results = await Promise.all(updatePromises);
      
      // Check if all updates were successful
      const allSuccessful = results.every(result => result.success !== false);
      
      if (allSuccessful) {
        setSaveNotification({
          open: true,
          message: t('recordUpdatedSuccessfully') || 'Record updated successfully',
          severity: 'success'
        });
        handleEditDialogClose();
        
        // Refresh data
        if (typeof onDataChange === 'function') {
          await onDataChange();
        }
      } else {
        const failedFields = results
          .map((result, index) => ({ result, field: Object.keys(changedFields)[index] }))
          .filter(({ result }) => result.success === false)
          .map(({ field }) => field);
        
        setSaveNotification({
          open: true,
          message: `Failed to update fields: ${failedFields.join(', ')}`,
          severity: 'error'
        });
      }
    } catch (error) {
      logger.error('Error saving edited record:', error);
      setSaveNotification({
        open: true,
        message: t('errorSavingRecord') || 'Error saving record',
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Add outreach status cell render function
  const renderOutreachStatus = (row) => {
    try {
      // More detailed debug information
      logger.log("Outreach status for record:", row.record_id);
      logger.log("Complete outreach_status object:", JSON.stringify(row.outreach_status, null, 2));

      // If this record is expected to have outreach data but doesn't, log it specially
      if (!row.outreach_status || !row.outreach_status.has_outreach) {
        // Check if this is one of the records we expect to have outreach data
        if (row.Instellingsnaam && row.Instellingsnaam.includes("Example")) {
          logger.warn("Expected outreach data missing for:", row.record_id, row.Instellingsnaam);
          
          // You can add a special icon for debugging this specific case
          return (
            <Tooltip title="Expected outreach data missing">
              <PhoneIcon color="warning" fontSize="small" />
            </Tooltip>
          );
        }
        logger.log("No outreach status for record:", row.record_id);
        return null;
      }

      const outreachStatus = row.outreach_status;
      const phoneNumber = outreachStatus.outreach_method === 'phone'
        ? (outreachStatus.contact_detail || '')
        : '';

      const withPhone = (icon, tooltip) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Tooltip title={tooltip}>{icon}</Tooltip>
          {phoneNumber && (
            <Typography variant="body2" component="span" sx={{ whiteSpace: 'nowrap' }}>
              {phoneNumber}
            </Typography>
          )}
        </Box>
      );

      // Display meeting icon if meeting is scheduled
      if (outreachStatus.meeting_scheduled) {
        const meetingDate = outreachStatus.meeting_date ? new Date(outreachStatus.meeting_date).toLocaleDateString() : 'Date not specified';
        logger.log(`Rendering meeting icon with date: ${meetingDate}`);
        return withPhone(
          <EventIcon color="success" fontSize="small" />,
          `Meeting scheduled on ${meetingDate}`
        );
      }
      
      // Display follow-up icon if follow-up is scheduled
      if (outreachStatus.followup_date) {
        const followupDate = new Date(outreachStatus.followup_date).toLocaleDateString();
        logger.log(`Rendering followup icon with date: ${followupDate}`);
        return withPhone(
          <ScheduleIcon color="info" fontSize="small" />,
          `Follow-up scheduled on ${followupDate}`
        );
      }

      // Otherwise, display outreach method icon
      let Icon;
      switch (outreachStatus.outreach_method) {
        case "phone":
          Icon = PhoneIcon;
          break;
        case "email":
          Icon = EmailIcon;
          break;
        case "linkedin":
          Icon = LinkedInIcon;
          break;
        default:
          Icon = PhoneIcon;
      }

      // Determine color based on reached status
      let color;
      let tooltip;
      if (outreachStatus.reached_intended_contact) {
        color = "warning";
        tooltip = "Contact reached";
      } else {
        color = "disabled";
        tooltip = "Contact attempted, not reached";
      }


      return withPhone(
        <Icon color={color} fontSize="small" />,
        tooltip
      );
    } catch (error) {
      logger.error("Error rendering outreach status:", error, row);
      // Fallback rendering with warning color to indicate an issue
      return (
        <Tooltip title="Error displaying outreach status">
          <PhoneIcon color="error" fontSize="small" />
        </Tooltip>
      );
    }
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  // Modify the toolbar to include the Research button and Outreach button
  const renderToolbar = () => {
    const totalUserCols = (tableSettings?.columnOrder || []).length;
    const shownCols = visibleColumns.length;

    return (
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: '2px solid',
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.background.default, 0.6),
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TableChartIcon sx={{ color: 'text.secondary', fontSize: '1.3rem' }} />
          <Typography variant="subtitle1" component="div" sx={{ fontWeight: 600 }}>
            {t('Data Table')}
          </Typography>
          {loading && (
            <CircularProgress size={20} sx={{ ml: 1 }} />
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: 'center' }}>
          {/* Column settings button */}
          <Tooltip title={`Columns (${shownCols}/${totalUserCols})`}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ViewWeekIcon />}
                onClick={(e) => setColumnPanelAnchor(e.currentTarget)}
                sx={{ textTransform: 'none', fontWeight: 500, minWidth: 0 }}
              >
                {shownCols}/{totalUserCols}
              </Button>
            </span>
          </Tooltip>

          {/* Refresh button */}
          <Tooltip title={t('refreshData')}>
            <span>
              <IconButton 
                onClick={handleRefresh} 
                disabled={loading || isRefreshing}
                size="small"
                sx={{ 
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                {isRefreshing ? 
                  <CircularProgress size={18} /> : 
                  <RefreshIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                }
              </IconButton>
            </span>
          </Tooltip>
          
          {/* Sales Outreach button */}
          <Tooltip title={t('outreach')}>
            <span>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                startIcon={<ContactsIcon />}
                onClick={handleOutreachClick}
                disabled={selectedRows.length !== 1 || loading}
                sx={{ textTransform: 'none' }}
              >
                {t('outreach')}
              </Button>
            </span>
          </Tooltip>
          
          {/* Research button */}
          <Tooltip title={t('research')}>
            <span>
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<SearchIcon />}
                onClick={handleResearchClick}
                disabled={selectedRows.length === 0 || loading}
                sx={{ textTransform: 'none' }}
              >
                {t('research')}
              </Button>
            </span>
          </Tooltip>
          
          {user?.is_admin || isSupervisor ? (
            <>
              <Tooltip title={t('bulkAssign')}>
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={<BookmarkAddIcon />}
                    onClick={() => setBulkClaimDialogOpen(true)}
                    disabled={loading}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('bulkAssign')} ({selectedRows.length})
                  </Button>
                </span>
              </Tooltip>
              
              {user?.is_admin ? (
                <Tooltip title={t('claimSelected')}>
                  <span>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<BookmarkAddIcon />}
                      onClick={handleClaimRecords}
                      disabled={selectedRows.length === 0 || loading}
                      sx={{ textTransform: 'none' }}
                    >
                      {t('claimSelected')} ({selectedRows.length})
                    </Button>
                  </span>
                </Tooltip>
              ) : null}
            </>
          ) : null}
        </Box>
      </Box>
    );
  };

  return (
    <>
      {/* Move notification outside of Paper to ensure it's visible */}
      <Snackbar
        open={refreshNotification.open}
        autoHideDuration={5000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ 
          '& .MuiSnackbarContent-root': { 
            minWidth: '300px' 
          }
        }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity="success" 
          variant="filled"
          sx={{ 
            width: '100%',
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box>
              {refreshNotification.message}
            </Box>
            <Box sx={{ ml: 2, fontWeight: 'bold' }}>
              {refreshNotification.count} {t('records')}
            </Box>
          </Box>
        </Alert>
      </Snackbar>

      {/* Save notification */}
      <Snackbar
        open={saveNotification.open}
        autoHideDuration={5000}
        onClose={() => setSaveNotification({ ...saveNotification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSaveNotification({ ...saveNotification, open: false })} 
          severity={saveNotification.severity}
          variant="filled"
        >
          {saveNotification.message}
        </Alert>
      </Snackbar>
      
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        {renderToolbar()}
        
        {loading ? (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <CircularProgress />
            </Box>

            <Box>
              {Array.from(new Array(5)).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rectangular"
                  height={40}
                  sx={{ my: 0.5 }}
                />
              ))}
            </Box>
          </Box>
        ) : !data || data.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body1" color="textSecondary">
              {t('noData')}
            </Typography>
          </Paper>
        ) : (
          <>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                maxHeight: "70vh",
                overflow: "auto",
              }}
            >
              <Table stickyHeader sx={{ minWidth: 650 }} size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      '& .MuiTableCell-head': {
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        bgcolor: theme.palette.grey[50],
                        borderBottom: '2px solid',
                        borderColor: 'divider',
                      },
                    }}
                  >
                    {/* Selection Column */}
                    <TableCell
                      padding="checkbox"
                      sx={{
                        width: 48,
                        minWidth: 48,
                        maxWidth: 48,
                      }}
                    >
                      <Checkbox
                        size="small"
                        indeterminate={
                          selectedRows.length > 0 &&
                          selectedRows.length <
                            data.filter((row) => {
                              const isClaimed = row.claimed === "Yes" || row.claimed === true || row.claimed === "true";
                              const claimedByCurrentUser = isClaimed && hasClaimByCurrentUser(row);
                              const claimedBySupervisedUser = isClaimed && isClaimedBySupervisedUser(row);
                              return row.record_id && (user?.is_admin || !isClaimed || claimedByCurrentUser || claimedBySupervisedUser);
                            }).length
                        }
                        checked={
                          data.filter((row) => {
                              const isClaimed = row.claimed === "Yes" || row.claimed === true || row.claimed === "true";
                              const claimedByCurrentUser = isClaimed && hasClaimByCurrentUser(row);
                              const claimedBySupervisedUser = isClaimed && isClaimedBySupervisedUser(row);
                              return row.record_id && (user?.is_admin || !isClaimed || claimedByCurrentUser || claimedBySupervisedUser);
                          }).length > 0 &&
                          selectedRows.length ===
                            data.filter((row) => {
                              const isClaimed = row.claimed === "Yes" || row.claimed === true || row.claimed === "true";
                              const claimedByCurrentUser = isClaimed && hasClaimByCurrentUser(row);
                              const claimedBySupervisedUser = isClaimed && isClaimedBySupervisedUser(row);
                              return row.record_id && (user?.is_admin || !isClaimed || claimedByCurrentUser || claimedBySupervisedUser);
                            }).length
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            const selectableRowIds = data
                              .filter((row) => {
                                const isClaimed = row.claimed === "Yes" || row.claimed === true || row.claimed === "true";
                                const claimedByCurrentUser = isClaimed && hasClaimByCurrentUser(row);
                                const claimedBySupervisedUser = isClaimed && isClaimedBySupervisedUser(row);
                                return row.record_id && (user?.is_admin || !isClaimed || claimedByCurrentUser || claimedBySupervisedUser);
                              })
                              .map((row) => row.record_id);
                            setSelectedRows(selectableRowIds);
                          } else {
                            setSelectedRows([]);
                          }
                        }}
                      />
                    </TableCell>

                    {/* Outreach Status Column */}
                    <TableCell
                      align="left"
                      sx={{
                        width: 'auto',
                        minWidth: 48,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <Tooltip title={t('outreachStatus')}>
                        <PhoneIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                      </Tooltip>
                    </TableCell>

                    {/* Edit Column */}
                    <TableCell
                      align="center"
                      padding="checkbox"
                      sx={{
                        width: 40,
                        minWidth: 40,
                        maxWidth: 40,
                      }}
                    >
                      <Tooltip title={t('edit') || 'Edit'}>
                        <EditIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                      </Tooltip>
                    </TableCell>

                    {/* Claimed By Column */}
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: 'text.secondary',
                        width: 140,
                        minWidth: 100,
                      }}
                    >
                      {onSort ? (
                        <TableSortLabel
                          active={searchParams.sortField === 'claimed'}
                          direction={searchParams.sortField === 'claimed' ? (searchParams.sortOrder || 'asc') : 'asc'}
                          onClick={() => onSort('claimed')}
                          sx={{ fontSize: 'inherit', fontWeight: 'inherit', textTransform: 'inherit', letterSpacing: 'inherit' }}
                        >
                          {t('claimedBy')}
                        </TableSortLabel>
                      ) : t('claimedBy')}
                    </TableCell>

                    {/* Pinned column (Instellingsnaam) if enabled */}
                    {pinnedColumn && visibleColumns.includes(pinnedColumn) && (
                      <TableCell
                        data-col={pinnedColumn}
                        sx={{
                          '&&': { position: 'sticky', left: 0, zIndex: 3 },
                          boxShadow: '2px 0 4px rgba(0,0,0,0.06)',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          color: 'text.primary',
                          ...(tableSettings?.columnWidths?.[pinnedColumn] ? {
                            width: tableSettings.columnWidths[pinnedColumn],
                            minWidth: tableSettings.columnWidths[pinnedColumn],
                            maxWidth: tableSettings.columnWidths[pinnedColumn],
                          } : { minWidth: 180 }),
                          pr: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {onSort ? (
                            <TableSortLabel
                              active={searchParams.sortField === pinnedColumn}
                              direction={searchParams.sortField === pinnedColumn ? (searchParams.sortOrder || 'asc') : 'asc'}
                              onClick={() => onSort(pinnedColumn)}
                              sx={{ fontSize: 'inherit', fontWeight: 'inherit', textTransform: 'inherit', letterSpacing: 'inherit' }}
                            >
                              {t(pinnedColumn)}
                            </TableSortLabel>
                          ) : t(pinnedColumn)}
                          <Box
                            onPointerDown={(e) => handleResizeStart(e, pinnedColumn)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: 4,
                              cursor: 'col-resize',
                              '&:hover': { bgcolor: 'primary.main', opacity: 0.3 },
                            }}
                          />
                        </Box>
                      </TableCell>
                    )}

                    {/* Other data columns */}
                    {visibleColumns
                      .filter((col) => col !== pinnedColumn)
                      .map((column) => (
                        <TableCell
                          key={column}
                          data-col={column}
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            color: 'text.secondary',
                            position: 'relative',
                            pr: 2,
                            ...(tableSettings?.columnWidths?.[column] ? {
                              width: tableSettings.columnWidths[column],
                              minWidth: tableSettings.columnWidths[column],
                              maxWidth: tableSettings.columnWidths[column],
                            } : { minWidth: 100 }),
                          }}
                        >
                          {onSort ? (
                            <TableSortLabel
                              active={searchParams.sortField === column}
                              direction={searchParams.sortField === column ? (searchParams.sortOrder || 'asc') : 'asc'}
                              onClick={() => onSort(column)}
                              sx={{ fontSize: 'inherit', fontWeight: 'inherit', textTransform: 'inherit', letterSpacing: 'inherit' }}
                            >
                              {t(column)}
                            </TableSortLabel>
                          ) : t(column)}
                          <Box
                            onPointerDown={(e) => handleResizeStart(e, column)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: 4,
                              cursor: 'col-resize',
                              '&:hover': { bgcolor: 'primary.main', opacity: 0.3 },
                            }}
                          />
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {data.map((row, rowIndex) => {
                    const isClaimed =
                      row.claimed === "Yes" || row.claimed === true || row.claimed === "true";
                    const claimedByCurrentUser =
                      isClaimed && hasClaimByCurrentUser(row);
                    const claimedBySupervisedUser =
                      isClaimed && isClaimedBySupervisedUser(row);
                    const isDisabled = isClaimed && !claimedByCurrentUser && !user?.is_admin && !claimedBySupervisedUser;

                    return (
                      <TableRow
                        key={row.record_id || rowIndex}
                        hover={!isDisabled}
                        selected={row.record_id && isRowSelected(row.record_id)}
                        onClick={(event) =>
                          !isDisabled &&
                          handleRowClick(event, row.record_id, rowIndex)
                        }
                        onDoubleClick={() => !isDisabled && handleRowDoubleClick(row)}
                        sx={{
                          transition: 'background-color 120ms ease',
                          "&:nth-of-type(odd)": {
                            backgroundColor: isDisabled
                              ? "rgba(220, 220, 220, 0.5)"
                              : 'rgba(0, 0, 0, 0.015)',
                          },
                          ...(isClaimed && !user?.is_admin && !claimedBySupervisedUser && {
                            backgroundColor: "rgba(220, 220, 220, 0.7)",
                            opacity: 0.75,
                            color: "text.disabled",
                            pointerEvents: isDisabled ? "none" : "auto",
                            "& .MuiTableCell-root": {
                              borderColor: "rgba(224, 224, 224, 0.6)",
                            },
                          }),
                          ...(isClaimed && user?.is_admin && !claimedByCurrentUser && {
                            backgroundColor: "rgba(230, 230, 230, 0.3)",
                            borderLeft: "3px solid",
                            borderLeftColor: "primary.light",
                          }),
                          ...(isClaimed && claimedBySupervisedUser && !user?.is_admin && {
                            backgroundColor: "rgba(240, 248, 255, 0.5)",
                            borderLeft: "3px solid",
                            borderLeftColor: "info.light",
                          }),
                          cursor: isDisabled ? "default" : "pointer",
                        }}
                      >
                        {/* Selection Cell */}
                        <TableCell padding="checkbox">
                          {user && (
                            <Checkbox
                              size="small"
                              color="primary"
                              checked={row.record_id && isRowSelected(row.record_id)}
                              disabled={isDisabled}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                e.stopPropagation();
                                !isDisabled &&
                                  handleRowClick(
                                    { ...e, shiftKey: e.shiftKey },
                                    row.record_id,
                                    rowIndex
                                  );
                              }}
                            />
                          )}
                        </TableCell>

                        {/* Outreach Status Cell */}
                        <TableCell
                          align="left"
                          onClick={(e) => e.stopPropagation()}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          {renderOutreachStatus(row)}
                        </TableCell>

                        {/* Edit Cell */}
                        <TableCell
                          align="center"
                          padding="checkbox"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Tooltip title={t('edit') || 'Edit'}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleEditClick(e, row)}
                              disabled={isDisabled}
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>

                        {/* Claimed Cell */}
                        <TableCell
                          onClick={(e) => e.stopPropagation()}
                        >
                          {renderClaimedCell(row)}
                        </TableCell>

                        {/* Pinned column cell */}
                        {pinnedColumn && visibleColumns.includes(pinnedColumn) && (
                          <TableCell
                            data-col={pinnedColumn}
                            sx={{
                              position: 'sticky',
                              left: 0,
                              zIndex: 1,
                              bgcolor: 'background.paper',
                              boxShadow: '2px 0 4px rgba(0,0,0,0.06)',
                              fontWeight: 500,
                              color: 'text.primary',
                              ...(tableSettings?.columnWidths?.[pinnedColumn] ? {
                                width: tableSettings.columnWidths[pinnedColumn],
                                minWidth: tableSettings.columnWidths[pinnedColumn],
                                maxWidth: tableSettings.columnWidths[pinnedColumn],
                              } : { minWidth: 180 }),
                            }}
                          >
                            {formatCellValue(
                              row[pinnedColumn],
                              columnTypes && columnTypes[pinnedColumn]
                            )}
                          </TableCell>
                        )}

                        {/* Other data columns */}
                        {visibleColumns
                          .filter((col) => col !== pinnedColumn)
                          .map((column, colIndex) => (
                            <TableCell
                              key={`${rowIndex}-${column || colIndex}`}
                              data-col={column}
                              align={
                                columnTypes && columnTypes[column] === "numeric"
                                  ? "right"
                                  : "left"
                              }
                              sx={{
                                color: 'text.secondary',
                                ...(tableSettings?.columnWidths?.[column] ? {
                                  width: tableSettings.columnWidths[column],
                                  minWidth: tableSettings.columnWidths[column],
                                  maxWidth: tableSettings.columnWidths[column],
                                } : { minWidth: 100 }),
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {formatCellValue(
                                row[column],
                                columnTypes && columnTypes[column]
                              )}
                            </TableCell>
                          ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Controls */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
                position: "relative", // Add for positioning
                willChange: "transform", // Optimize rendering
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="body2" sx={{ mr: 2 }}>
                  {t('rowsPerPage')}:
                </Typography>
                <FormControl size="small" variant="outlined">
                  <Select
                    value={pageSize}
                    onChange={(e) => onPageSizeChange(e.target.value)}
                    sx={{ minWidth: 80 }}
                  >
                    {[10, 25, 50, 100].map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(e, page) => onPageChange(page)}
                color="primary"
                siblingCount={1}
                boundaryCount={1}
              />
            </Box>
          </>
        )}

        {/* Add Research Dialog */}
        <Dialog
          open={researchDialogOpen}
          onClose={() => setResearchDialogOpen(false)}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: { height: "90vh" }
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <CompanyResearch 
              selectedRecords={selectedRecordsData} 
              onClose={() => setResearchDialogOpen(false)} 
            />
          </DialogContent>
        </Dialog>

        {/* Add Bulk Claim Dialog */}
        <Dialog
          open={bulkClaimDialogOpen}
          onClose={() => {
            setBulkClaimDialogOpen(false);
            setRemoveExistingAssignments(false);
            setAssignAllRecords(false);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('bulkAssignRecords')}</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom>
                {assignAllRecords 
                  ? `${t('selectUsersToAssign')} (${totalRows} ${t('allRecords') || 'all records'} ${t('matchingFilters') || 'matching current filters'})`
                  : `${t('selectUsersToAssign')} (${selectedRows.length} ${t('recordsSelected')})`
                }
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={assignAllRecords}
                    onChange={(e) => setAssignAllRecords(e.target.checked)}
                    color="primary"
                  />
                }
                label={t('assignAllRecords') || 'Assign all records (regardless of pagination)'}
                sx={{ mt: 2 }}
              />
              {assignAllRecords && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {t('assignAllRecordsInfo') || `This will assign all ${totalRows} records matching your current search and filters to the selected users.`}
                </Alert>
              )}
              <Autocomplete
                multiple
                options={availableUsers}
                getOptionLabel={(option) => option.username}
                value={selectedUsers}
                onChange={(event, newValue) => setSelectedUsers(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('selectUsers')}
                    placeholder={t('selectUsers')}
                  />
                )}
                sx={{ mt: 2 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={removeExistingAssignments}
                    onChange={(e) => setRemoveExistingAssignments(e.target.checked)}
                    color="primary"
                  />
                }
                label={t('removeExistingAssignments') || 'Remove existing assignments before assigning'}
                sx={{ mt: 2 }}
              />
              {removeExistingAssignments && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {t('removeExistingWarning') || 'Warning: This will remove all existing assignments for the selected records before assigning them to the selected users.'}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setBulkClaimDialogOpen(false);
              setRemoveExistingAssignments(false);
              setAssignAllRecords(false);
            }}>
              {t('cancel')}
            </Button>
            <Button
              onClick={handleBulkClaimSubmit}
              variant="contained"
              color="primary"
              disabled={(selectedRows.length === 0 && !assignAllRecords) || selectedUsers.length === 0 || isRefreshing}
            >
              {isRefreshing ? t('assigning') : t('assign')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Sales Outreach */}
        {selectedRecordsData.length > 0 && (
          <SalesOutreachShell
            open={outreachDialogOpen}
            onClose={() => setOutreachDialogOpen(false)}
            recordId={selectedRecordsData[0].record_id}
            companyName={selectedRecordsData[0].Instellingsnaam || selectedRecordsData[0].company_name || 'Unknown Company'}
            contactInfo={selectedRecordsData[0]}
          />
        )}

        {/* Add Edit Record Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { maxHeight: "90vh" }
          }}
        >
          <DialogTitle>
            {t('editRecord') || 'Edit Record'} {editingRecord && `#${editingRecord.record_id}`}
          </DialogTitle>
          <DialogContent>
            {editingRecord && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('editRecordFields') || 'Edit the fields below. Only changed fields will be saved.'}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    maxHeight: '60vh',
                    overflowY: 'auto',
                    pr: 1
                  }}
                >
                  {(() => {
                    // Get all fields from the record, prioritizing columns but including all fields
                    const skipFields = ['claimed', 'claimed_by', 'Claimed_by', 'record_id', 'claims', 'outreach_status', 'file_id', 'created_at', 'updated_at', 'updated_by'];
                    const allFields = new Set([
                      ...columns.filter(col => !skipFields.includes(col)),
                      ...Object.keys(editedFields).filter(key => !skipFields.includes(key))
                    ]);
                    
                    // Define priority fields order (support both Notes and Notities)
                    const priorityFields = ['Instellingsnaam', 'Notes', 'Notities', 'Filter1', 'Filter2', 'Filter3'];
                    
                    // Separate fields into priority and rest
                    const priorityFieldsList = priorityFields.filter(field => allFields.has(field));
                    const restFields = Array.from(allFields).filter(field => !priorityFields.includes(field));
                    
                    // Combine: priority fields first, then rest
                    // Make sure Notes/Notities comes right after Instellingsnaam
                    const notesField = priorityFieldsList.find(f => f === 'Notes' || f === 'Notities');
                    const otherPriorityFields = priorityFieldsList.filter(f => f !== 'Notes' && f !== 'Notities' && f !== 'Instellingsnaam');
                    const orderedFields = [
                      ...priorityFieldsList.filter(f => f === 'Instellingsnaam'),
                      ...(notesField ? [notesField] : []),
                      ...otherPriorityFields,
                      ...restFields
                    ];
                    
                    return orderedFields.map((column) => {
                      const columnType = columnTypes && columnTypes[column];
                      const value = editedFields[column] ?? '';
                      const isNotesField = column === 'Notes' || column === 'Notities';
                      
                      return (
                        <TextField
                          key={column}
                          label={t(column) || column}
                          value={value}
                          onChange={(e) => handleFieldChange(column, e.target.value)}
                          fullWidth
                          variant="outlined"
                          size="small"
                          type={columnType === 'date' ? 'date' : columnType === 'numeric' ? 'number' : 'text'}
                          InputLabelProps={columnType === 'date' ? { shrink: true } : {}}
                          multiline={isNotesField || String(value).length > 50}
                          rows={isNotesField ? 3 : String(value).length > 50 ? 3 : 1}
                          sx={isNotesField ? {
                            '& .MuiInputBase-root': {
                              resize: 'vertical',
                              minHeight: '80px'
                            }
                          } : {}}
                        />
                      );
                    });
                  })()}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleEditDialogClose}
              startIcon={<CancelIcon />}
              disabled={isSaving}
            >
              {t('cancel') || 'Cancel'}
            </Button>
            <Button
              onClick={handleSaveEdit}
              variant="contained"
              color="primary"
              startIcon={!isSaving && <SaveIcon />}
              disabled={isSaving}
            >
              {isSaving ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  {t('saving') || 'Saving...'}
                </Box>
              ) : (
                t('save') || 'Save'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Column Settings Panel */}
        <ColumnSettingsPanel
          anchorEl={columnPanelAnchor}
          open={columnPanelOpen}
          onClose={() => setColumnPanelAnchor(null)}
          settings={tableSettings}
          allColumns={columns}
          systemColumns={SYSTEM_COLUMNS}
          onToggleColumn={toggleColumn}
          onReorderColumns={reorderColumns}
          onApplyPreset={applyPreset}
          onResetToDefault={resetToDefault}
          onTogglePinFirstColumn={togglePinFirstColumn}
          translateColumn={t}
        />
      </Paper>
    </>
  );
};

export default React.memo(DataGrid);
